import { useEffect, useState } from "react";
import { decodeTokenService } from "../services/decodeTokenService";
import { userExperimentsService } from "../services/userExperiments/userExperimentsService";

const FINANCE_MODULE_FLAG = "finance_module";

const cachedFinanceEnabledByOwner: Record<string, boolean> = {};
const inFlightRequestByOwner: Record<string, Promise<boolean> | undefined> = {};

const isLocalFinanceEnabled = (): boolean => {
  if (!import.meta.env.DEV) return false;
  if (!import.meta.env.VITE_LOCAL_OWNER_ID) return false;

  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
};

const isEnabledValue = (value: unknown): boolean => {
  if (value === true) return true;
  if (typeof value === "string") return value.trim().toLowerCase() === "true";
  return false;
};

const loadFinanceModuleFlag = async (ownerId: string): Promise<boolean> => {
  if (cachedFinanceEnabledByOwner[ownerId] !== undefined) {
    return cachedFinanceEnabledByOwner[ownerId];
  }

  if (!inFlightRequestByOwner[ownerId]) {
    inFlightRequestByOwner[ownerId] = userExperimentsService
      .getExperiments()
      .then((response) => {
        const enabled = isEnabledValue(response.experiments?.[FINANCE_MODULE_FLAG]?.enabled);
        cachedFinanceEnabledByOwner[ownerId] = enabled;
        return enabled;
      })
      .catch((error) => {
        console.error("Error fetching finance_module flag:", error);
        cachedFinanceEnabledByOwner[ownerId] = false;
        return false;
      })
      .finally(() => {
        inFlightRequestByOwner[ownerId] = undefined;
      });
  }

  return inFlightRequestByOwner[ownerId];
};

export const useFinanceModuleFlag = () => {
  const ownerId = decodeTokenService.getUserIdFromToken();
  const localFinanceEnabled = isLocalFinanceEnabled();
  const [enabled, setEnabled] = useState(() =>
    localFinanceEnabled || (ownerId ? cachedFinanceEnabledByOwner[ownerId] === true : false)
  );
  const [loading, setLoading] = useState(() =>
    localFinanceEnabled ? false : ownerId ? cachedFinanceEnabledByOwner[ownerId] === undefined : false
  );

  useEffect(() => {
    let active = true;

    if (localFinanceEnabled) {
      setEnabled(true);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    if (!ownerId) {
      setEnabled(false);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(cachedFinanceEnabledByOwner[ownerId] === undefined);
    loadFinanceModuleFlag(ownerId).then((nextEnabled) => {
      if (!active) return;
      setEnabled(nextEnabled);
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [localFinanceEnabled, ownerId]);

  return { enabled, loading };
};
